import { el, field, iconBtn, note, segmented, type DesignerCtx, type IconName } from './panels.js';
import { createStepper } from './controls.js';
import { run } from './commands.js';
import { isFixedTextBox, minWrapWidthMm, setTextBoxWidth } from './text-box.js';

/**
 * "Poravnanje" — Zazzle's Alignment panel, opened from the contextual toolbar
 * into the rail's panel slot.
 *
 * Five blocks in Zazzle's own order: align (six buttons plus the radio that
 * says whether "centre" means the zone or the other selected elements),
 * distribute, flip, scale, rotate. Everything runs through one command path,
 * so an alignment is one undo step whether it moved one layer or six.
 */

interface Reference {
  value: 'zone' | 'selection';
}

/** Per-designer memory: the radio must not reset every time the panel repaints. */
const references = new WeakMap<DesignerCtx, Reference>();

function referenceOf(ctx: DesignerCtx): Reference {
  const existing = references.get(ctx);
  if (existing) return existing;
  // Zazzle defaults to "design": a lone element centres in the zone, which is
  // what a shopper who presses "centre" once means.
  const fresh: Reference = { value: 'zone' };
  references.set(ctx, fresh);
  return fresh;
}

export function renderAlignmentPanel(body: HTMLElement, ctx: DesignerCtx): void {
  const reference = referenceOf(ctx);
  const ids = (): string[] => {
    const selected = ctx.selected();
    if (ctx.selection.size) return [...ctx.selection];
    return selected ? [selected.id] : [];
  };
  const anchor = ctx.selected();

  if (!ids().length) {
    body.append(note(ctx.texts.selectLayer));
    return;
  }
  if (ctx.selection.size > 1) {
    body.append(note(ctx.texts.multiSelected.replace('{n}', String(ctx.selection.size))));
  }

  // ── Align ────────────────────────────────────────────────────────────────
  const alignBox = field(ctx.texts.alignSection);
  const alignRow = el('div', 'pzd-icon-row');
  const directions: Array<[IconName, string, 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom']> = [
    ['alignVLeft', ctx.texts.alignLeft, 'left'],
    ['alignVCenter', ctx.texts.alignCenter, 'center'],
    ['alignVRight', ctx.texts.alignRight, 'right'],
    ['alignTop', ctx.texts.alignTop, 'top'],
    ['alignMiddle', ctx.texts.alignMiddle, 'middle'],
    ['alignBottom', ctx.texts.alignBottom, 'bottom'],
  ];
  for (const [icon, label, direction] of directions) {
    alignRow.append(iconBtn(icon, label, () => {
      run(ctx, { op: 'align', ids: ids(), direction, reference: reference.value });
    }));
  }
  alignBox.wrap.append(alignRow, referenceRow(ctx, reference));
  body.append(alignBox.wrap);

  // ── Distribute ───────────────────────────────────────────────────────────
  const spreadBox = field(ctx.texts.distributeSection);
  const spreadRow = el('div', 'pzd-icon-row');
  spreadRow.append(
    iconBtn('distributeH', ctx.texts.distributeH, () => run(ctx, { op: 'distribute', ids: ids(), axis: 'x' }),
      { disabled: ids().length < 3 }),
    iconBtn('distributeV', ctx.texts.distributeV, () => run(ctx, { op: 'distribute', ids: ids(), axis: 'y' }),
      { disabled: ids().length < 3 }),
  );
  spreadBox.wrap.append(spreadRow);
  body.append(spreadBox.wrap);

  // ── Flip ─────────────────────────────────────────────────────────────────
  const flipBox = field(ctx.texts.flipSection);
  const flipRow = el('div', 'pzd-icon-row');
  flipRow.append(
    iconBtn('flipH', ctx.texts.horizontal, () => run(ctx, { op: 'flip', ids: ids(), axis: 'x' })),
    iconBtn('flipV', ctx.texts.vertical, () => run(ctx, { op: 'flip', ids: ids(), axis: 'y' })),
  );
  flipBox.wrap.append(flipRow);
  body.append(flipBox.wrap);

  // ── Scale ────────────────────────────────────────────────────────────────
  const scaleBox = field(ctx.texts.scaleSection);
  const scaleRow = el('div', 'pzd-icon-row');
  scaleRow.append(
    iconBtn('minus', ctx.texts.stepLess, () => run(ctx, { op: 'scale', ids: ids(), factor: 0.9 })),
    iconBtn('plus', ctx.texts.stepMore, () => run(ctx, { op: 'scale', ids: ids(), factor: 1.1 })),
  );
  scaleBox.wrap.append(scaleRow);
  body.append(scaleBox.wrap);

  // ── Rotate ───────────────────────────────────────────────────────────────
  const rotateBox = field(ctx.texts.rotateSection);
  const rotateRow = el('div', 'pzd-icon-row');
  const turn = (degrees: number): void => {
    run(ctx, { op: 'rotate', ids: ids(), degrees });
  };
  const ccw = iconBtn('rotate', ctx.texts.rotateCcw, () => turn(-15));
  ccw.classList.add('is-mirrored');
  rotateRow.append(ccw, iconBtn('rotate', ctx.texts.rotateCw, () => turn(15)));
  rotateBox.wrap.append(rotateRow);
  if (anchor) {
    // The number is the element's own angle, not a relative turn: that is what
    // "Rotation 0" means in Zazzle, and it is the only way to type 45 exactly.
    const current = Math.round(anchor.box.rotation ?? 0);
    rotateBox.wrap.append(createStepper({
      label: ctx.texts.rotationAngle,
      value: current,
      min: -360,
      max: 360,
      step: 1,
      onCommit: (value) => run(ctx, { op: 'place', ids: [anchor.id], box: { ...anchor.box, rotation: value } }),
    }).element);
  }
  body.append(rotateBox.wrap);

  // ── Position and size ────────────────────────────────────────────────────
  if (anchor) {
    const box = anchor.box;
    const proportional = ctx.view.lockAspect || Boolean(anchor.groupId)
      || !['image', 'shape'].includes(anchor.kind);
    const fixedColumn = isFixedTextBox(anchor);
    const place = (next: typeof box): void => {
      run(ctx, { op: 'place', ids: [anchor.id], box: next });
    };
    const sizeBox = field(ctx.texts.positionSection);
    sizeBox.wrap.append(
      createStepper({
        // A wrap column's width is the column, not a stretch: typing a number
        // here re-flows the words and lets the height follow, exactly as the
        // side handle does. Everything else keeps the placement command.
        label: fixedColumn ? ctx.texts.wrapWidth : ctx.texts.widthMm,
        value: box.w,
        min: fixedColumn && anchor.kind === 'text' ? minWrapWidthMm(anchor.sizeMm) : 0.5,
        max: Math.max(ctx.config.sizeMm.w * 3, box.w),
        step: 0.5,
        onCommit: (w) => {
          if (fixedColumn) {
            setTextBoxWidth(ctx, anchor, w, `wrap:${anchor.id}`);
            return;
          }
          place({ ...box, w, h: proportional ? (box.h * w) / box.w : box.h });
        },
      }).element,
      // A wrap column's height is the lines, so there is no number to type:
      // a field that snapped back to the measured height on every commit
      // would read as a broken control rather than an explained one.
      ...(fixedColumn ? [] : [createStepper({
        label: ctx.texts.heightMm,
        value: box.h,
        min: 0.5,
        max: Math.max(ctx.config.sizeMm.h * 3, box.h),
        step: 0.5,
        onCommit: (h) => place({ ...box, h, w: proportional ? (box.w * h) / box.h : box.w }),
      }).element]),
      createStepper({
        label: ctx.texts.posX,
        value: box.x,
        min: 0,
        max: ctx.config.sizeMm.w,
        step: 0.5,
        onCommit: (x) => place({ ...box, x }),
      }).element,
      createStepper({
        label: ctx.texts.posY,
        value: box.y,
        min: 0,
        max: ctx.config.sizeMm.h,
        step: 0.5,
        onCommit: (y) => place({ ...box, y }),
      }).element,
    );
    body.append(sizeBox.wrap);
  }

  // ── Group ────────────────────────────────────────────────────────────────
  const groupBox = field(ctx.texts.groupSection);
  const groupRow = el('div', 'pzd-icon-row');
  groupRow.append(
    iconBtn('group', ctx.texts.group, () => run(ctx, { op: 'group', ids: ids() }), { disabled: ids().length < 2 }),
    iconBtn('ungroup', ctx.texts.ungroup, () => run(ctx, { op: 'ungroup', ids: ids() }),
      { disabled: !ids().some((id) => ctx.store.value().layers.find((layer) => layer.id === id)?.groupId) }),
  );
  groupBox.wrap.append(groupRow);
  body.append(groupBox.wrap);
}

function referenceRow(ctx: DesignerCtx, reference: Reference): HTMLElement {
  const box = el('div', 'pzd-reference');
  box.append(el('span', 'pzd-hint', ctx.texts.relativeTo));
  box.append(segmented([
    { id: 'selection', label: ctx.texts.relativeObjects, on: reference.value === 'selection' },
    { id: 'zone', label: ctx.texts.relativeDesign, on: reference.value === 'zone' },
  ], (value) => {
    reference.value = value === 'selection' ? 'selection' : 'zone';
    ctx.refreshPanel();
  }));
  return box;
}
